<template>
    <div
        :class="{
            'cursor-text focus-within:shadow-[0_0_0_1px_rgba(44,84,234,1)] hover:bg-[#fafafa] hover:shadow-[0_0_0_1px_rgba(44,84,234,1)] focus:bg-[#fafafb]':
                editable,
            'rounded bg-[#fafafb] p-3 shadow-[0_0_0_1px_rgba(44,84,234,1)]': active,
            'shadow-[0_0_0_1px_rgba(204,0,48,1)]!': invalidFeedback,
        }"
        class="flex rounded-[4px] p-0 transition-all duration-100"
        v-bind="attrs"
    >
        <textarea
            v-if="active && editable"
            ref="textarea"
            :class="{
                'is-invalid': invalidFeedback,
                truncate: rows === 1,
            }"
            :name="name"
            :placeholder="placeholder"
            :readonly="!editable"
            :rows="rows"
            :style="{
                minHeight: `${minHeight}px`,
            }"
            :value="modelValue"
            class="w-100 resize-none! border-0! outline-0"
            @blur="blur"
            @input="update($event.target.value)"
            @keydown="handleKeyDown($event)"
        />
        <div
            v-else
            ref="textarea"
            :class="{
                'is-invalid': invalidFeedback,
            }"
            :data-testid="name"
            :style="{ minHeight: `${minHeight}px` }"
            class="w-100"
            v-html="htmlContent"
            @click.prevent="activate($event)"
        />
    </div>
    <div v-if="invalidFeedback" class="text-small font-normal text-danger" v-text="invalidFeedback" />
</template>
<script setup>
import { computed, getCurrentInstance, nextTick, onMounted, ref, useAttrs } from 'vue';
import { useResizeObserver } from '@vueuse/core';
import { pipe } from '@/utils.js';

defineOptions({
    inheritAttrs: false,
});

const attrs = useAttrs();

const props = defineProps({
    modelValue: {},
    name: {
        type: String,
    },
    editable: {
        type: Boolean,
        default: true,
    },
    rows: {
        type: Number,
        default: 1,
    },
    placeholder: {},
    lineHeight: {
        type: Number,
        default: 32,
    },
    maxHeight: {
        type: Number,
        default: null,
    },
    invalidFeedback: {},
    resizable: {
        type: Boolean,
        default: true,
    },
    bordered: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits(['update:modelValue']);

const active = ref(false);
const height = ref(0);
const textarea = ref(null);
const lastObservedWidth = ref(0);

const minHeight = computed(() => props.rows * props.lineHeight);

const instance = getCurrentInstance();
const filters = instance?.proxy?.$filters;

const htmlContent = computed(() => {
    if (!props.modelValue && !!props.placeholder) {
        return `<span class="text-muted">${props.placeholder}</span>`;
    }

    const linkify = filters?.linkify ?? ((v) => v);
    const nl2br = filters?.nl2br ?? ((v) => v);

    return pipe(props.modelValue).to(linkify).to(nl2br).value;
});

const isFieldResizable = computed(() => props.resizable && props.rows > 1);

onMounted(() => {
    resize();
});

// The field can mount before its modal is laid out (hidden, zero width), so the
// initial resize() measures a collapsed scrollHeight and a long value overflows
// until the first focus re-measures it. Re-run resize() once the element gains
// its real width. Guarded on width only; resize() mutates height, so reacting to
// height changes would loop.
useResizeObserver(textarea, ([{ contentRect }]) => {
    if (contentRect.width === lastObservedWidth.value) {
        return;
    }

    lastObservedWidth.value = contentRect.width;
    resize();
});

function activate(event) {
    if (!props.editable) return;

    event.stopImmediatePropagation();

    if (event.target.nodeName === 'A') {
        return;
    }

    active.value = true;

    nextTick(() => {
        if (textarea.value) {
            textarea.value.focus();
        }
        resize();
    });
}

function blur() {
    const nextValue = typeof props.modelValue === 'string' ? props.modelValue?.trim() : props.modelValue;
    update(nextValue);

    nextTick(() => {
        active.value = false;
        resize();
    });
}

function setActive(shouldActivate) {
    if (shouldActivate) {
        if (!props.editable) return;

        active.value = true;

        nextTick(() => {
            if (textarea.value) {
                textarea.value.focus();
            }
            resize();
        });
    } else {
        blur();
    }
}

function resize() {
    const element = textarea.value;

    if (!isFieldResizable.value || !element) {
        return;
    }

    const min = minHeight.value;
    element.style.height = `${min}px`;

    const contentHeight = element.scrollHeight > min ? element.scrollHeight : min;
    const max = props.maxHeight !== null ? Math.max(props.maxHeight, min) : null;
    const capped = max !== null && contentHeight > max;

    height.value = capped ? max : contentHeight;
    element.style.height = `${height.value}px`;
    element.style.overflowY = capped ? 'auto' : '';
}

function update(value) {
    emit('update:modelValue', value);
    resize();
}

function handleKeyDown(event) {
    switch (event.keyCode) {
        case 13:
            if (!isFieldResizable.value) {
                event.preventDefault();
                blur();
            }
            break;
        case 27:
            event.preventDefault();
            blur();
            break;
    }
}

defineExpose({
    setActive,
});
</script>
